import {
  atomStorage,
  atomStorageKey,
  BaseService,
  type BaseServiceContext,
  getEventSenderMetadata,
} from '@bangle.io/base-utils';
import type {
  ThemeConfig,
  ThemeManager,
} from '@bangle.io/color-scheme-manager';
import {
  applyColorTheme,
  applyCommentFont,
  applyEditorFont,
  colorThemeMode,
  DEFAULT_COLOR_THEME,
  DEFAULT_COMMENT_FONT,
  DEFAULT_EDITOR_FONT,
  isAssetLocationPreference,
  isColorThemePreference,
  isCommentFontPreference,
  isEditorFontPreference,
  isReaderRoutePathname,
  READER_DEFAULT_COLOR_THEME,
  SERVICE_NAME,
  SIDEBAR_DEFAULT_WIDTH,
} from '@bangle.io/constants';
import { T } from '@bangle.io/mini-js-utils';
import type {
  AssetLocationPreference,
  BaseSyncDatabaseService,
  ColorThemePreference,
  CommentFontPreference,
  EditorFontPreference,
  ScopedEmitter,
} from '@bangle.io/types';
import type {
  AppAlertDialogProps,
  DialogSingleInputProps,
  DialogSingleSelectProps,
} from '@bangle.io/ui-components';
import { atom, type PrimitiveAtom } from 'jotai';
import { atomEffect } from 'jotai-effect';
import {
  type FileTreeExpandedPathsByWorkspace,
  type FileTreeExpansionAction,
  reduceFileTreeExpansion,
} from './file-tree-expansion-state';

type Route = 'omni-home' | 'omni-command' | 'omni-filtered';

/** Sync-database key suffix of `$colorThemePreference` (see `atomStorage`). */
const COLOR_THEME_PREFERENCE_STORAGE_KEY = 'color-theme-preference';

const AssetLocationPreferenceValidator = {
  validate: isAssetLocationPreference,
  typeName: 'asset-location-preference',
};

const CommentFontPreferenceValidator = {
  validate: isCommentFontPreference,
  typeName: 'comment-font-preference',
};

const ColorThemePreferenceValidator = {
  validate: isColorThemePreference,
  typeName: 'color-theme-preference',
};

const EditorFontPreferenceValidator = {
  validate: isEditorFontPreference,
  typeName: 'editor-font-preference',
};

/**
 * Column visibility overrides for the workspace notes table, keyed by column
 * id. Columns absent from the record use the table's own defaults.
 */
export type NotesTableColumnVisibility = Record<string, boolean>;

const NotesTableColumnVisibilityValidator = {
  validate: (value: unknown): value is NotesTableColumnVisibility =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((visible) => typeof visible === 'boolean'),
  typeName: 'notes-table-column-visibility',
};

const FileTreeExpandedPathsByWorkspaceValidator = {
  validate: (value: unknown): value is FileTreeExpandedPathsByWorkspace =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(
      (paths) =>
        Array.isArray(paths) && paths.every((path) => typeof path === 'string'),
    ),
  typeName: 'file-tree-expanded-paths-by-workspace',
};

function determineOmniSearchRoute(input: string, currentRoute: Route): Route {
  switch (currentRoute) {
    case 'omni-home': {
      if (input.startsWith('>')) {
        return 'omni-command';
      }
      return 'omni-filtered';
    }
    case 'omni-command': {
      if (!input.startsWith('>')) {
        if (input.trim() === '') {
          return 'omni-home';
        }
        return 'omni-filtered';
      }
      return 'omni-command';
    }
    case 'omni-filtered': {
      if (input.trim() === '') {
        return 'omni-home';
      }
      return 'omni-filtered';
    }
    default: {
      return 'omni-home';
    }
  }
}

/**
 * Manages UI state such as theme preferences, dialogs, and omni-search state
 */
export class WorkbenchStateService extends BaseService {
  static deps = ['syncDatabase'] as const;

  private $_wideEditor: PrimitiveAtom<boolean> | undefined;
  private $_sidebarOpen: PrimitiveAtom<boolean> | undefined;
  private $_sidebarWidth: PrimitiveAtom<number> | undefined;
  private $_linkedMentionsCollapsed: PrimitiveAtom<boolean> | undefined;
  private $_localGraphCollapsed: PrimitiveAtom<boolean> | undefined;
  private $_pwaOpenInAppPromptSeen: PrimitiveAtom<boolean> | undefined;
  private $_fileTreeExpandedPathsByWorkspace:
    | PrimitiveAtom<FileTreeExpandedPathsByWorkspace>
    | undefined;
  private $_assetLocationPreference:
    | PrimitiveAtom<AssetLocationPreference>
    | undefined;
  private $_commentFontPreference:
    | PrimitiveAtom<CommentFontPreference>
    | undefined;
  private $_colorThemePreference:
    | PrimitiveAtom<ColorThemePreference>
    | undefined;
  private $_editorFontPreference:
    | PrimitiveAtom<EditorFontPreference>
    | undefined;
  private $_notesTableColumnVisibility:
    | PrimitiveAtom<NotesTableColumnVisibility>
    | undefined;

  $openWsDialog = atom(false);
  $openOmniSearch = atom(false);
  $themePref = atom<ThemeConfig['defaultPreference']>('system');
  /**
   * True once this tab is known to run an older app version than another tab
   * (storage schema handshake). The app blocks the tab behind a reload
   * dialog; there is deliberately no way to unset it short of reloading.
   */
  $staleTab = atom(false);
  $singleInputDialog = atom<
    | undefined
    | ({
        dialogId: `dialog::${string}`;
      } & Omit<DialogSingleInputProps, 'open' | 'setOpen'>)
  >(undefined);
  $singleSelectDialog = atom<
    | undefined
    | ({
        dialogId: `dialog::${string}`;
      } & Omit<DialogSingleSelectProps, 'open' | 'setOpen'>)
  >(undefined);
  $alertDialog = atom<
    | undefined
    | ({ dialogId: `dialog::${string}` } & Omit<
        AppAlertDialogProps,
        'open' | 'setOpen'
      >)
  >();
  $omniSearchInput = atom('');
  $omniSearchRoute = atom<Route>('omni-home');
  $openAllFiles = atom(false);
  $allFilesSearchInput = atom('');

  $cleanSearchTerm = atom((get) => {
    const search = get(this.$omniSearchInput);
    const route = get(this.$omniSearchRoute);

    if (route === 'omni-command') {
      return search.slice(1).trim().toLowerCase();
    }
    return search.trim().toLowerCase();
  });

  constructor(
    context: BaseServiceContext,
    private dep: {
      syncDatabase: BaseSyncDatabaseService;
    },
    private config: {
      themeManager: ThemeManager;
      emitter: ScopedEmitter<'event::app:reload-ui' | 'event::app:stale-tab'>;
    },
  ) {
    super(SERVICE_NAME.workbenchStateService, context, dep);
    this.store.set(this.$themePref, this.config.themeManager.currentPreference);
  }

  hookMount() {
    this.seedReaderThemeDefault();
    this.config.emitter.on(
      'event::app:stale-tab',
      () => {
        this.store.set(this.$staleTab, true);
      },
      this.abortSignal,
    );
    this.addCleanup(
      this.config.themeManager.onThemeChange(({ preference }) => {
        this.store.set(this.$themePref, preference);
      }),
      this.store.sub(
        atomEffect((get, set) => {
          const open = get(this.$openAllFiles);
          if (!open) {
            set(this.$allFilesSearchInput, '');
          }
        }),
        () => {},
      ),
      this.store.sub(
        atomEffect((get, set) => {
          const open = get(this.$openOmniSearch);
          if (!open) {
            set(this.$omniSearchInput, '');
          }
        }),
        () => {},
      ),
      this.store.sub(this.$omniSearchInput, () => {
        const input = this.store.get(this.$omniSearchInput);
        const currentRoute = this.store.get(this.$omniSearchRoute);
        const newRoute = determineOmniSearchRoute(input, currentRoute);
        if (newRoute !== currentRoute) {
          this.store.set(this.$omniSearchRoute, newRoute);
        }
      }),
      this.store.sub(
        atomEffect((get) => {
          applyCommentFont(get(this.$commentFontPreference));
        }),
        () => {},
      ),
      this.store.sub(
        atomEffect((get) => {
          const themeId = get(this.$colorThemePreference);
          applyColorTheme(themeId);
          const mode = colorThemeMode(themeId);
          if (this.config.themeManager.currentPreference !== mode) {
            this.config.themeManager.setPreference(mode);
          }
        }),
        () => {},
      ),
      this.store.sub(
        atomEffect((get) => {
          applyEditorFont(get(this.$editorFontPreference));
        }),
        () => {},
      ),
    );
  }

  /**
   * Reader links open in Dark until this browser has chosen a theme, so a
   * shared note never opens in the app's untouched Light default. Seeding
   * writes the shared preference: opening a reader link therefore also
   * switches an app whose theme was never customized, which is the intended
   * trade-off for keeping one preference across app and reader.
   *
   * Runs before the theme effect is subscribed, so the theme is applied as
   * Dark once and nothing paints Light in between.
   */
  private seedReaderThemeDefault(): void {
    // Tests may run without a browser location; then there is no reader route.
    const pathname =
      typeof location === 'undefined' ? undefined : location.pathname;

    if (!pathname || !isReaderRoutePathname(pathname)) {
      return;
    }

    const stored = this.dep.syncDatabase.getEntry(
      atomStorageKey(this.name, COLOR_THEME_PREFERENCE_STORAGE_KEY),
      { tableName: 'sync' },
    );

    if (stored.found) {
      return;
    }

    this.store.set(this.$colorThemePreference, READER_DEFAULT_COLOR_THEME);
  }

  public changeThemePreference(preference: ThemeConfig['defaultPreference']) {
    this.config.themeManager.setPreference(preference);
  }

  public resetOmniSearch() {
    this.store.set(this.$omniSearchInput, '');
    this.store.set(this.$omniSearchRoute, 'omni-home');
  }

  public goToCommandRoute() {
    this.store.set(this.$openOmniSearch, true);
    this.store.set(this.$omniSearchInput, '>');
  }

  /** True while a modal prompt should keep keyboard focus out of the editor. */
  public blocksEditorFocus(): boolean {
    return Boolean(
      this.store.get(this.$singleInputDialog) ||
        this.store.get(this.$singleSelectDialog) ||
        this.store.get(this.$alertDialog),
    );
  }

  public reloadUi() {
    this.config.emitter.emit('event::app:reload-ui', {
      sender: getEventSenderMetadata({ tag: this.name }),
    });
  }

  public setFileTreeDirectoryExpanded(
    workspaceName: string,
    path: string,
    expanded: boolean,
  ) {
    this.updateFileTreeExpansion({
      type: 'set-directory',
      workspaceName,
      path,
      expanded,
    });
  }

  public revealFileTreePaths(workspaceName: string, paths: readonly string[]) {
    this.updateFileTreeExpansion({ type: 'reveal', workspaceName, paths });
  }

  public collapseFileTree(
    workspaceName: string,
    keepExpandedPaths: readonly string[],
  ) {
    this.updateFileTreeExpansion({
      type: 'collapse-all',
      workspaceName,
      keepExpandedPaths,
    });
  }

  private updateFileTreeExpansion(action: FileTreeExpansionAction) {
    this.store.set(this.$fileTreeExpandedPathsByWorkspace, (current) =>
      reduceFileTreeExpansion(current, action),
    );
  }

  get $wideEditor() {
    if (!this.$_wideEditor) {
      this.$_wideEditor = atomStorage({
        serviceName: this.name,
        key: 'wide-editor',
        initValue: true,
        syncDb: this.dep.syncDatabase,
        validator: T.Boolean,
        logger: this.logger,
      });
    }
    return this.$_wideEditor;
  }

  get $sidebarOpen() {
    if (!this.$_sidebarOpen) {
      this.$_sidebarOpen = atomStorage({
        serviceName: this.name,
        key: 'sidebar-open',
        initValue: true,
        syncDb: this.dep.syncDatabase,
        validator: T.Boolean,
        logger: this.logger,
      });
    }
    return this.$_sidebarOpen;
  }

  get $sidebarWidth() {
    if (!this.$_sidebarWidth) {
      this.$_sidebarWidth = atomStorage({
        serviceName: this.name,
        key: 'sidebar-width',
        initValue: SIDEBAR_DEFAULT_WIDTH,
        syncDb: this.dep.syncDatabase,
        validator: {
          validate: (value): value is number =>
            typeof value === 'number' && Number.isFinite(value),
          typeName: 'finite-number',
        },
        logger: this.logger,
      });
    }
    return this.$_sidebarWidth;
  }

  get $linkedMentionsCollapsed() {
    if (!this.$_linkedMentionsCollapsed) {
      this.$_linkedMentionsCollapsed = atomStorage({
        serviceName: this.name,
        key: 'linked-mentions-collapsed',
        initValue: false,
        syncDb: this.dep.syncDatabase,
        validator: T.Boolean,
        logger: this.logger,
      });
    }
    return this.$_linkedMentionsCollapsed;
  }

  get $localGraphCollapsed() {
    if (!this.$_localGraphCollapsed) {
      this.$_localGraphCollapsed = atomStorage({
        serviceName: this.name,
        key: 'local-graph-collapsed',
        initValue: true,
        syncDb: this.dep.syncDatabase,
        validator: T.Boolean,
        logger: this.logger,
      });
    }
    return this.$_localGraphCollapsed;
  }

  get $pwaOpenInAppPromptSeen() {
    if (!this.$_pwaOpenInAppPromptSeen) {
      this.$_pwaOpenInAppPromptSeen = atomStorage({
        serviceName: this.name,
        key: 'pwa-open-in-app-prompt-seen',
        initValue: false,
        syncDb: this.dep.syncDatabase,
        validator: T.Boolean,
        logger: this.logger,
      });
    }
    return this.$_pwaOpenInAppPromptSeen;
  }

  get $fileTreeExpandedPathsByWorkspace() {
    if (!this.$_fileTreeExpandedPathsByWorkspace) {
      this.$_fileTreeExpandedPathsByWorkspace = atomStorage({
        serviceName: this.name,
        key: 'file-tree-expanded-paths-by-workspace',
        initValue: {},
        syncDb: this.dep.syncDatabase,
        validator: FileTreeExpandedPathsByWorkspaceValidator,
        logger: this.logger,
      });
    }
    return this.$_fileTreeExpandedPathsByWorkspace;
  }

  get $notesTableColumnVisibility() {
    if (!this.$_notesTableColumnVisibility) {
      this.$_notesTableColumnVisibility = atomStorage({
        serviceName: this.name,
        key: 'notes-table-column-visibility',
        initValue: {},
        syncDb: this.dep.syncDatabase,
        validator: NotesTableColumnVisibilityValidator,
        logger: this.logger,
      });
    }
    return this.$_notesTableColumnVisibility;
  }

  get $assetLocationPreference() {
    if (!this.$_assetLocationPreference) {
      this.$_assetLocationPreference = atomStorage({
        serviceName: this.name,
        key: 'asset-location-preference',
        initValue: 'assets-folder',
        syncDb: this.dep.syncDatabase,
        validator: AssetLocationPreferenceValidator,
        logger: this.logger,
      });
    }
    return this.$_assetLocationPreference;
  }

  get $commentFontPreference() {
    if (!this.$_commentFontPreference) {
      this.$_commentFontPreference = atomStorage({
        serviceName: this.name,
        key: 'comment-font-preference',
        initValue: DEFAULT_COMMENT_FONT,
        syncDb: this.dep.syncDatabase,
        validator: CommentFontPreferenceValidator,
        logger: this.logger,
      });
    }
    return this.$_commentFontPreference;
  }

  get $colorThemePreference() {
    if (!this.$_colorThemePreference) {
      this.$_colorThemePreference = atomStorage({
        serviceName: this.name,
        key: COLOR_THEME_PREFERENCE_STORAGE_KEY,
        initValue: DEFAULT_COLOR_THEME,
        syncDb: this.dep.syncDatabase,
        validator: ColorThemePreferenceValidator,
        logger: this.logger,
      });
    }
    return this.$_colorThemePreference;
  }

  get $editorFontPreference() {
    if (!this.$_editorFontPreference) {
      this.$_editorFontPreference = atomStorage({
        serviceName: this.name,
        key: 'editor-font-preference',
        initValue: DEFAULT_EDITOR_FONT,
        syncDb: this.dep.syncDatabase,
        validator: EditorFontPreferenceValidator,
        logger: this.logger,
      });
    }
    return this.$_editorFontPreference;
  }
}
